import { CustomChartSpec } from "./types";
import { getDisplayCurrencySymbol } from "../../utils/stockPriceFormatter";
import { formatNumber } from "../Dashboard/Charts/utils/formatNumber";

/**
 * Y-axis / value / tooltip formatter for chat chart specs, using the app's
 * shared utils (packages/ui/CLAUDE.md). Every value carries its unit —
 * $1.2B / 44.7% / 34.6× — with the sign before the symbol for negatives.
 * Every branch goes through formatNumber(Math.abs(...)): it rounds, and its
 * suffix chain is sign-blind, so negatives format on their magnitude.
 * A supplied `currency` implies currency mode even when yFormat says "number"
 * (observed live: currency "USD" + yFormat "number" rendered bare floats).
 * Kept free of component imports so it stays unit-testable under CRA's jest
 * (importing the chart component drags Nivo's untransformed ESM into the run).
 */
export const makeYFormat =
  (spec: CustomChartSpec) =>
  (value: number): string => {
    const magnitude = formatNumber(Math.abs(value));
    const isCurrency =
      spec.yFormat === "currency" ||
      (Boolean(spec.currency) && spec.yFormat !== "percent" && spec.yFormat !== "multiple");
    if (isCurrency) {
      const symbol = getDisplayCurrencySymbol(spec.currency ?? "USD");
      return value < 0 ? `-${symbol}${magnitude}` : `${symbol}${magnitude}`;
    }
    if (spec.yFormat === "percent") {
      return value < 0 ? `-${magnitude}%` : `${magnitude}%`;
    }
    if (spec.yFormat === "multiple") {
      return value < 0 ? `-${magnitude}×` : `${magnitude}×`;
    }
    return value < 0 ? `-${magnitude}` : magnitude;
  };
